/**
 * WHYSHIFT Sticky ATC — show when hero buy box scrolls away; mirrors hero price/availability.
 * Add-to-bag delegates to the hero product form so CartAddEvent flows through theme cart drawer.
 */
class WhyshiftStickyAtc extends HTMLElement {
  /** @type {HTMLElement | null} */
  #hero = null;

  /** @type {HTMLElement | null} */
  #observeTarget = null;

  /** @type {HTMLElement | null} */
  #bar = null;

  /** @type {HTMLElement | null} */
  #priceEl = null;

  /** @type {HTMLButtonElement | null} */
  #button = null;

  /** @type {HTMLElement | null} */
  #labelEl = null;

  /** @type {HTMLElement | null} */
  #heroPrice = null;

  /** @type {HTMLButtonElement | null} */
  #heroAtc = null;

  /** @type {IntersectionObserver | null} */
  #heroObserver = null;

  /** @type {IntersectionObserver | null} */
  #footerObserver = null;

  /** @type {MutationObserver | null} */
  #priceObserver = null;

  /** @type {boolean} */
  #isStuck = false;

  /** @type {boolean} */
  #hiddenByFooter = false;

  /** @type {AbortController} */
  #abortController = new AbortController();

  connectedCallback() {
    this.#hero = document.getElementById('whyshift-product-hero');
    if (!this.#hero) return;

    this.#bar = this.querySelector('[data-whyshift-sticky-bar]');
    this.#priceEl = this.querySelector('[data-whyshift-sticky-price]');
    this.#button = this.querySelector('[data-whyshift-sticky-atc]');
    this.#labelEl = this.querySelector('[data-whyshift-sticky-atc-label]');
    this.#heroPrice =
      this.#hero.querySelector('product-price [ref="priceContainer"]') ||
      this.#hero.querySelector('[data-whyshift-price]');
    this.#observeTarget =
      this.#hero.querySelector('[data-whyshift-buy-box]') ||
      this.#hero.querySelector('.whyshift-product-hero__price-row') ||
      this.#hero.querySelector('[data-whyshift-product-form]') ||
      this.#hero;

    this.#cacheHeroAtc();
    this.#setupHeroObserver();
    this.#setupFooterObserver();
    this.#setupPriceObserver();
    this.#syncFromHero();

    const { signal } = this.#abortController;

    this.#button?.addEventListener('click', this.#handleClick, { signal });
    document.addEventListener('variant:update', this.#handleVariantUpdate, { signal });
    document.addEventListener('cart:update', this.#handleCartComplete, { signal });
    document.addEventListener('cart:error', this.#handleCartComplete, { signal });
  }

  disconnectedCallback() {
    this.#abortController.abort();
    this.#heroObserver?.disconnect();
    this.#footerObserver?.disconnect();
    this.#priceObserver?.disconnect();
  }

  #cacheHeroAtc() {
    this.#heroAtc = this.#hero?.querySelector('[ref="addToCartButton"]') ?? null;
  }

  #setupHeroObserver() {
    if (!this.#observeTarget) return;

    this.#heroObserver = new IntersectionObserver((entries) => {
      const [entry] = entries;
      if (!entry) return;

      if (!entry.isIntersecting && !this.#isStuck && !this.#hiddenByFooter) {
        const rect = entry.target.getBoundingClientRect();

        if (rect.bottom < 0 || rect.top < 0) {
          this.#show();
        }
      } else if (entry.isIntersecting && this.#isStuck) {
        this.#hiddenByFooter = false;
        this.#hide();
      }
    });

    this.#heroObserver.observe(this.#observeTarget);
  }

  #setupFooterObserver() {
    const footer =
      document.querySelector('footer') ?? document.querySelector('[class*="footer-group"]');

    if (!footer || !this.#observeTarget) return;

    this.#footerObserver = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        if (!entry) return;

        if (entry.isIntersecting && this.#isStuck) {
          this.#hiddenByFooter = true;
          this.#hide();
          return;
        }

        if (!entry.isIntersecting && this.#hiddenByFooter) {
          const rect = this.#observeTarget.getBoundingClientRect();

          if (rect.bottom < 0 || rect.top < 0) {
            this.#hiddenByFooter = false;
            this.#show();
          }
        }
      },
      { rootMargin: '200px 0px 0px 0px' }
    );

    this.#footerObserver.observe(footer);
  }

  #setupPriceObserver() {
    if (!this.#heroPrice) return;

    this.#priceObserver = new MutationObserver(() => {
      this.#syncFromHero();
    });

    this.#priceObserver.observe(this.#heroPrice, {
      characterData: true,
      childList: true,
      subtree: true,
    });
  }

  #handleClick = () => {
    this.#cacheHeroAtc();

    if (!this.#heroAtc || this.#heroAtc.disabled) return;

    this.#heroAtc.dataset.puppet = 'true';
    this.#heroAtc.click();
  };

  #handleVariantUpdate = (event) => {
    const productId = this.dataset.productId;

    if (!productId || event.detail?.data?.productId !== productId) return;

    window.requestAnimationFrame(() => {
      this.#cacheHeroAtc();
      this.#syncFromHero();

      if (event.detail?.resource) {
        this.dataset.variantAvailable = String(event.detail.resource.available ?? false);
      }
    });
  };

  #handleCartComplete = () => {
    this.#cacheHeroAtc();

    if (this.#heroAtc) {
      this.#heroAtc.dataset.puppet = 'false';
    }
  };

  #syncFromHero() {
    if (this.#heroPrice && this.#priceEl) {
      const salePrice =
        this.#heroPrice.querySelector('.price-item--sale, .price-item--regular') || this.#heroPrice;
      this.#priceEl.textContent = salePrice.textContent?.trim() || this.#priceEl.textContent;
    }

    if (!this.#heroAtc || !this.#button) return;

    this.#button.disabled = this.#heroAtc.disabled;

    if (this.#labelEl) {
      const heroLabel = this.#heroAtc.querySelector('.add-to-cart-text__content span span span');

      if (heroLabel?.textContent?.trim()) {
        this.#labelEl.textContent = heroLabel.textContent.trim();
      }
    }
  }

  #show() {
    if (this.#isStuck) return;

    this.#isStuck = true;
    this.dataset.stuck = 'true';
    this.#bar?.setAttribute('aria-hidden', 'false');
    this.#syncFromHero();
  }

  #hide() {
    if (!this.#isStuck) return;

    this.#isStuck = false;
    this.dataset.stuck = 'false';
    this.#bar?.setAttribute('aria-hidden', 'true');
  }
}

if (!customElements.get('whyshift-sticky-atc')) {
  customElements.define('whyshift-sticky-atc', WhyshiftStickyAtc);
}
